import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import supabase from 'embroil/supabase';

import type Owner from '@ember/owner';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

interface MetricsService {
  identify(id: string, properties: Record<string, unknown>): void;
  reset(): void;
}

export default class CurrentUser extends Service {
  @service declare metrics: MetricsService;
  @tracked session: Session | null = null;

  constructor(owner: Owner) {
    super(owner);

    supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const appMetadata = session.user.app_metadata as Record<
            string,
            unknown
          >;
          this.metrics.identify(session.user.id, {
            email: session.user.email,
            name: appMetadata['nickname'] || appMetadata['full_name'],
            identities: session.user.identities,
            metadata: session.user.user_metadata,
          });
        } else if (event === 'SIGNED_OUT') {
          this.metrics.reset();
        }
        this.session = session || null;
      },
    );
  }

  get isLoggedIn() {
    return !!(this.session?.access_token && this.session?.user);
  }

  get user() {
    return this.session?.user;
  }

  get displayName() {
    return this.session?.user?.user_metadata['full_name'] as
      | string
      | undefined;
  }

  get profilePhoto() {
    return this.session?.user?.user_metadata['avatar_url'] as
      | string
      | undefined;
  }
}

declare module '@ember/service' {
  interface Registry {
    currentUser: CurrentUser;
  }
}
